import { combineReducers } from "redux";
import { cloneDeep } from "lodash";

import * as actions from "../actions";

const initialState = {
  campaignStatus: {
    isFetching: false,
    error: null,
  },
  campaigns: {
    byId: {},
    count: 0,
  },
  expectedRecipientNumber: 0,
  customerEventsById: {},
  customerEventStatus: {
    isFetching: false,
    error: null,
  },
  customerEventReport: null,
  customerEventUserList: {
    customer: {},
    instagram: {},
  },
  templatesById: {},
  messageCampaignReport: {},
  // 카카오 발신프로필(다우 라이브 조회). store.kakao 는 "연동했는가", 이건 "지금 어떤 상태인가".
  // 조회 전(null)과 조회 결과 빈 객체({})는 다른 의미다 — 반드시 status.loaded 로 구분할 것.
  kakaoProfile: null,
  brandMessageStatus: {
    isFetching: false,
    error: null,
    loaded: false,
  },
  // 신청 응답 { unsubscribe?, evidence?, apply } — 다우 message 원문 표시용.
  brandMessageApply: null,
};

function campaignStatus(state = initialState.campaignStatus, action) {
  switch (action.type) {
    case actions.CREATE_CAMPAIGN:
    case actions.UPDATE_CAMPAIGN:
    case actions.CANCEL_CAMPAIGN:
    case actions.LAUNCH_CAMPAIGN:
    case actions.SEND_BARCODE_COUPON_CAMPAIGN:
    case actions.GET_CAMPAIGNS:
    case actions.GET_CAMPAIGN:
    case actions.GET_CAMPAIGN_WITH_REPORT:
    case actions.GET_EXPECTED_RECIPIENT_NUMBER:
    case actions.CREATE_OR_UPDATE_CAMPAIGN_TEMPLATE:
    case actions.DELETE_CAMPAIGN_TEMPLATE:
    case actions.RECEIVE_CAMPAIGN_TEMPLATES:
    case actions.GET_MESSAGE_CAMPAIGN_REPORT:
      return {
        isFetching: true,
        error: null,
      };
    case actions.CREATE_OR_UPDATE_CAMPAIGN_SUCCESS:
    case actions.CANCEL_CAMPAIGN_SUCCESS:
    case actions.LAUNCH_CAMPAIGN_SUCCESS:
    case actions.GET_CAMPAIGNS_SUCCESS:
    case actions.GET_CAMPAIGN_SUCCESS:
    case actions.SET_MESSAGE_CAMPAIGN_REPORT_SUCCESS:
      return initialState.campaignStatus;
    case actions.LAUNCH_CAMPAIGN_FAILURE:
    case actions.CREATE_OR_UPDATE_CAMPAIGN_FAILURE:
    case actions.CANCEL_CAMPAIGN_FAILURE:
    case actions.GET_CAMPAIGNS_FAILURE:
    case actions.GET_CAMPAIGN_FAILURE:
    case actions.SET_MESSAGE_CAMPAIGN_REPORT_FAILURE:
      return {
        isFetching: false,
        error: action.error,
      };
    default:
      return state;
  }
}

function campaigns(state = initialState.campaigns, action) {
  switch (action.type) {
    case actions.RECEIVE_CAMPAIGN:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.campaign.id]: cloneDeep(action.campaign),
        },
      };
    case actions.RECEIVE_CAMPAIGNS:
      return {
        byId: action.list.reduce((obj, item) => {
          obj[item.id] = item;
          return obj;
        }, {}),
        count: action.count,
      };
    case actions.SIGN_OUT:
      return initialState.campaigns;
    default:
      return state;
  }
}

function expectedRecipientNumber(
  state = initialState.expectedRecipientNumber,
  action,
) {
  switch (action.type) {
    case actions.SET_EXPECTED_RECIPIENT_NUMBER:
      return action.count;
    default:
      return state;
  }
}

function customerEventsById(state = initialState.customerEventsById, action) {
  switch (action.type) {
    case actions.RECEIVE_CUSTOMER_EVENTS:
      return action.eventsById;
    case actions.RECEIVE_CUSTOMER_EVENT:
      return { ...cloneDeep(state), [action.event.id]: action.event };
    case actions.SIGN_OUT:
      return initialState.customerEventsById;
    default:
      return state;
  }
}

function customerEventReport(state = initialState.customerEventReport, action) {
  switch (action.type) {
    case actions.RECEIVE_CUSTOMER_EVENT_REPORT:
      return action.report;
    case actions.SIGN_OUT:
    case actions.CLEAR_CUSTOMER_EVENT_REPORT:
      return initialState.customerEventReport;
    default:
      return state;
  }
}

function messageCampaignReport(
  state = initialState.messageCampaignReport,
  action,
) {
  switch (action.type) {
    case actions.RECEIVE_MESSAGE_CAMPAIGN_REPORT:
      return action.report;
    case actions.SIGN_OUT:
      return initialState.messageCampaignReport;
    default:
      return state;
  }
}

function customerEventUserList(
  state = initialState.customerEventUserList,
  action,
) {
  switch (action.type) {
    case actions.RECEIVE_CUSTOMER_STORY_LIST:
      return { ...cloneDeep(state), instagram: action.data };
    case actions.RECEIVE_CUSTOMER_EVENT_USER_LIST:
      return { ...cloneDeep(state), customer: action.data };
    case actions.SIGN_OUT:
    case actions.CLEAR_CUSTOMER_EVENT_USER_LIST:
      return initialState.customerEventUserList;
    default:
      return state;
  }
}

function customerEventStatus(state = initialState.customerEventStatus, action) {
  switch (action.type) {
    case actions.GET_CUSTOMER_EVENTS:
    case actions.CREATE_OR_UPDATE_CUSTOMER_EVENT:
    case actions.DELETE_CUSTOMER_EVENT:
    case actions.GET_CUSTOMER_EVENT_REPORT:
    case actions.GET_CUSTOMER_EVENT_USER_LIST:
      return {
        isFetching: true,
        error: null,
      };
    case actions.SET_CUSTOMER_EVENT_STATUS_SUCCESS:
      return initialState.customerEventStatus;
    case actions.SET_CUSTOMER_EVENT_STATUS_FAILURE:
      return {
        isFetching: false,
        error: action.error,
      };
    default:
      return state;
  }
}

function campaignTemplates(state = initialState.templatesById, action) {
  switch (action.type) {
    case actions.RECEIVE_CAMPAIGN_TEMPLATE:
      return {
        ...cloneDeep(state),
        [action.template.id]: cloneDeep(action.template),
      };
    case actions.RECEIVE_CAMPAIGN_TEMPLATES:
      return action.templates.reduce((obj, t) => {
        obj[t.id] = t;
        return obj;
      }, {});
    case actions.RECEIVE_DELETED_TEMPLATE:
      delete state[action.templateId];
      return state;
    case actions.SIGN_OUT:
      return initialState.templatesById;
    default:
      return state;
  }
}

function kakaoProfile(state = initialState.kakaoProfile, action) {
  switch (action.type) {
    case actions.RECEIVE_KAKAO_PROFILE:
      return action.profile || null;
    case actions.SIGN_OUT:
      return initialState.kakaoProfile;
    default:
      return state;
  }
}

// 조회/신청이 status 하나를 공유한다. 신청 성공 시 사가가 RECEIVE_BRAND_MESSAGE_APPLY 후
// RECEIVE_KAKAO_PROFILE 을 연달아 dispatch 하므로 최종 상태는 동일하다.
function brandMessageStatus(state = initialState.brandMessageStatus, action) {
  switch (action.type) {
    case actions.GET_KAKAO_PROFILE:
    case actions.APPLY_BRAND_MESSAGE:
      return {
        isFetching: true,
        error: null,
        loaded: state.loaded,
      };
    case actions.RECEIVE_KAKAO_PROFILE:
      return {
        isFetching: false,
        error: null,
        loaded: true,
      };
    case actions.RECEIVE_BRAND_MESSAGE_APPLY:
      return {
        ...state,
        isFetching: false,
        error: null,
      };
    case actions.RECEIVE_KAKAO_PROFILE_FAILURE:
    case actions.RECEIVE_BRAND_MESSAGE_APPLY_FAILURE:
      return {
        isFetching: false,
        error: action.error,
        loaded: state.loaded,
      };
    case actions.SIGN_OUT:
      return initialState.brandMessageStatus;
    default:
      return state;
  }
}

function brandMessageApply(state = initialState.brandMessageApply, action) {
  switch (action.type) {
    case actions.APPLY_BRAND_MESSAGE:
    case actions.RECEIVE_BRAND_MESSAGE_APPLY_FAILURE:
      return initialState.brandMessageApply;
    case actions.RECEIVE_BRAND_MESSAGE_APPLY:
      return action.result || null;
    case actions.SIGN_OUT:
      return initialState.brandMessageApply;
    default:
      return state;
  }
}

export const getGroupCampaignType = (type) => {
  switch (type) {
    case "general":
      return "고정형";
    case "random":
      return "랜덤형";
    default:
      return "";
  }
};

export const getEventEntryLabel = (condition) => {
  if (condition.memberOnly) {
    switch (condition.entryLimit) {
      case "once":
        return "한번만 가능";
      case "daily":
        return "매일 1번씩 가능";
      case "unlimited":
        return "무제한 참여가능";
      default:
        return "";
    }
  } else {
    return "제한없음";
  }
};

export const buildSuccessRate = (campaign) => {
  if (campaign.status === "done") {
    const total =
      campaign.recipientCount || (campaign.recipientList || []).length;
    let success = campaign.success;
    let rate = Math.round((success / (total || 1)) * 100);
    return `${rate}% (${success || 0}/${total})`;
  }
  return "";
};

export const getCampaignStatusLabel = (status) => {
  if (status === "requesting") {
    return { label: "발송중", color: "#ff6600" };
  }
  if (status === "requested") {
    return { label: "발송완료(결과수신중)", color: "#ff6600" };
  }
  if (status === "reserved") {
    return { label: "발송예정", color: "#ff6600" };
  }
  if (status === "draft" || status === "cancelled") {
    return { label: "편집 중", color: "#337ef3" };
  }
  if (status === "done") {
    return { label: "발송완료", color: "#009688" };
  }
  if (status === "failed") {
    return { label: "발송 실패", color: "#e02020" };
  }
  return { label: "", color: "#838b96" };
};

export default combineReducers({
  campaigns,
  campaignStatus,
  expectedRecipientNumber,
  customerEventsById,
  customerEventStatus,
  customerEventReport,
  customerEventUserList,
  campaignTemplates,
  messageCampaignReport,
  kakaoProfile,
  brandMessageStatus,
  brandMessageApply,
});
